import { create } from 'zustand'
import { useSimProgress } from '@/player/sim-progress'
import type { StocktakeProduct } from './mock-data'

export interface StocktakeEntry {
  id: string
  product: StocktakeProduct
  qty: number
}

interface StocktakeState {
  locationId: string
  entries: StocktakeEntry[]
  maxLocations: number
  /**
   * Has the count been opened on the Progress tab?
   *
   * Store state rather than `useState` because "Start stocktake" now navigates
   * to the scanner. Component-local state resets when the screen unmounts, so
   * a learner returning from a count would find the Progress tab closed again
   * — the unscanned banner hidden and Approve disabled — with the only way
   * forward being to tap Start, which would send them straight back to the
   * scanner. The guided player still forces this true via `?started=1`.
   */
  started: boolean
  addEntry: (product: StocktakeProduct, qty: number) => void
  removeEntry: (id: string) => void
  clear: () => void
  setMaxLocations: (n: number) => void
  setStarted: (started: boolean) => void
}

let entrySeq = 0

export const useStocktakeStore = create<StocktakeState>((set) => ({
  locationId: 'Loc 1',
  entries: [],
  maxLocations: 100,
  started: false,
  addEntry: (product, qty) =>
    set((state) => {
      entrySeq += 1
      return { entries: [...state.entries, { id: `entry-${entrySeq}`, product, qty }] }
    }),
  removeEntry: (id) => set((state) => ({ entries: state.entries.filter((e) => e.id !== id) })),
  clear: () => set({ entries: [] }),
  setMaxLocations: (n) => set({ maxLocations: n }),
  setStarted: (started) => set({ started }),
}))

/**
 * A new unguided run starts from a closed count.
 *
 * The tour-param sync in `StocktakeSetupScreen` covers the guided side, but an
 * unguided run has no params to clear `started` from — so without this, a
 * learner who finished guided lesson 4 (whose later steps carry `?started=1`)
 * opened the unguided run to a Progress tab that already showed counted rows,
 * with "Start stocktake" disabled and task `t4-05` impossible to tick.
 *
 * Subscribing here rather than resetting from the player keeps the dependency
 * pointing the way it already does: stocktake screens import from `@/player`,
 * never the reverse.
 *
 * It also clears the counted rows (`entries`) and the location fields back to
 * their initial values, so a re-entered count starts empty rather than showing
 * last session's rows.
 */
export const _unsubStocktakeSimReset = useSimProgress.subscribe((state, prev) => {
  if (state.simId !== prev.simId && state.simId !== null) {
    useStocktakeStore.setState({
      started: false,
      entries: [],
      locationId: 'Loc 1',
      maxLocations: 100,
    })
  }
})
